// Where the glyphs of one run of map text go (R548).
//
// The chart furniture writes text on the canvas as sprites: the atlas bakes
// one cell a glyph, and a piece draws one quad a glyph. This module is the
// arithmetic between the two. It knows nothing of the atlas, of WebGL or of
// the DOM: it takes an advance function and hands back a place a glyph.
//
// Two placements exist. `layoutRunInto` lays a run on a straight line, which is
// what a label beside a hull or a figure in a margin needs. `layoutAlongPath`
// walks a polyline by arc length and turns each glyph to the tangent, which
// is what a trench name along a rim and a legend along a ring need.
//
// One rule holds over both: a run always READS left to right. A path that
// runs the other way would set the run backwards and upside down, so
// `upright` reverses the order and turns every glyph by half a turn.

import { wrapAngle } from './bow';

/** What one glyph advances the pen, in px at the size the run is set at. */
export interface GlyphMetrics {
  advance(ch: string): number;
}

/** One placed glyph: its CENTRE, and the angle it is turned by. */
export interface GlyphPlace {
  ch: string;
  x: number;
  y: number;
  rot: number;
}

/** Where the run sits about the point it is laid out from. */
export type Anchor = 'left' | 'centre' | 'right';

/** One point of a polyline. */
export interface Pt {
  x: number;
  y: number;
}

/**
 * The width of a run, in px.
 *
 * `letterSpacing` stands BETWEEN two glyphs, so a run of n glyphs carries
 * n-1 of it and a run of one glyph carries none.
 */
export function runWidth(text: string, m: GlyphMetrics, letterSpacing = 0): number {
  let w = 0, n = 0;
  for (const ch of text) { w += m.advance(ch); n++; }
  return n === 0 ? 0 : w + letterSpacing * (n - 1);
}

/** Where the pen starts, against the point the run is laid out from. */
function penStart(anchor: Anchor, width: number): number {
  if (anchor === 'centre') return -width / 2;
  if (anchor === 'right') return -width;
  return 0;
}

/**
 * A run laid out into a scratch the caller keeps: the first `n` of `glyphs`
 * are the places of the last run, and the rest are the litter of an
 * earlier, longer run, kept so the next run writes into them again. A
 * piece that writes text every frame therefore allocates nothing a glyph.
 */
export interface RunScratch {
  glyphs: GlyphPlace[];
  n: number;
  width: number;
}

/** A scratch with nothing laid out in it. */
export function runScratch(): RunScratch {
  return { glyphs: [], n: 0, width: 0 };
}

/** Writes one place into the next slot of the scratch, reusing its object. */
function place(out: RunScratch, ch: string, x: number, y: number, rot: number): void {
  const g = out.glyphs[out.n++];
  if (g === undefined) out.glyphs.push({ ch, x, y, rot });
  else { g.ch = ch; g.x = x; g.y = y; g.rot = rot; }
}

/**
 * Lays a run on a straight line into a scratch: every glyph centre sits at
 * y = 0 and no glyph is turned.
 *
 * y = 0 is the vertical CENTRE of the cells and not the baseline, because a
 * cell is what the caller draws. The atlas records the baseline of a cell
 * (`glyphBase`) for a caller that must align a run by it.
 */
export function layoutRunInto(
  text: string, m: GlyphMetrics,
  opts: { anchor?: Anchor; letterSpacing?: number },
  out: RunScratch,
): void {
  const letterSpacing = opts.letterSpacing ?? 0;
  const width = runWidth(text, m, letterSpacing);
  out.n = 0;
  out.width = width;
  let pen = penStart(opts.anchor ?? 'left', width);
  for (const ch of text) {
    const adv = m.advance(ch);
    place(out, ch, pen + adv / 2, 0, 0);
    pen += adv + letterSpacing;
  }
}

/**
 * The length of a polyline, in the units of its points.
 *
 * A closed polyline carries the segment from the last point back to the
 * first, which is what a traced loop needs.
 */
export function polylineLength(pts: readonly Pt[], closed = false): number {
  if (pts.length < 2) return 0;
  let total = 0;
  for (let i = 1; i < pts.length; i++) total += dist(pts[i - 1], pts[i]);
  if (closed) total += dist(pts[pts.length - 1], pts[0]);
  return total;
}

function dist(a: Pt, b: Pt): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/**
 * Walks `s` px along a polyline and writes the point and the tangent angle.
 *
 * It writes into `out` and returns nothing, because a run asks for a point
 * a glyph and an object a glyph would be litter on the frame path.
 *
 * An open polyline CLAMPS: a negative `s` gives the first point and an `s`
 * past the end gives the last, each with the tangent of the end segment. A
 * closed polyline WRAPS instead, so a run round a loop never runs out.
 */
export function pointAlong(
  pts: readonly Pt[], s: number,
  out: { x: number; y: number; rot: number },
  closed = false,
): void {
  pointAlongOf(pts, polylineLength(pts, closed), s, out, closed);
}

/** `pointAlong` with the length of the polyline in hand: a run asks once a glyph. */
function pointAlongOf(
  pts: readonly Pt[], total: number, s: number,
  out: { x: number; y: number; rot: number }, closed: boolean,
): void {
  if (pts.length === 0) {
    out.x = 0; out.y = 0; out.rot = 0;
    return;
  }
  if (pts.length === 1) {
    out.x = pts[0].x; out.y = pts[0].y; out.rot = 0;
    return;
  }
  let at = closed && total > 0
    ? ((s % total) + total) % total
    : Math.max(0, Math.min(total, s));
  const last = closed ? pts.length : pts.length - 1;
  for (let i = 0; i < last; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % pts.length];
    const len = dist(a, b);
    if (len === 0) continue;
    if (at > len && i < last - 1) {
      at -= len;
      continue;
    }
    const t = at / len;
    out.x = a.x + (b.x - a.x) * t;
    out.y = a.y + (b.y - a.y) * t;
    out.rot = Math.atan2(b.y - a.y, b.x - a.x);
    return;
  }
  // Every segment had zero length, so the whole polyline is one point.
  out.x = pts[0].x; out.y = pts[0].y; out.rot = 0;
}

/** How many tangents `upright` reads over the span of a run. Two or more. */
const UPRIGHT_SAMPLES = 5;

/**
 * Lays a run along a polyline by arc length, turning each glyph to the
 * tangent under it.
 *
 * `startS` is the point along the path the run is laid out from, and
 * `anchor` says how the run sits about it: `left` starts there, `centre`
 * straddles it, `right` ends there.
 *
 * `upright` (on by default) reads the mean tangent over the span of the run.
 * Where it points left — where the mean cosine is under zero — the run is
 * REVERSED along the path and every glyph is turned by half a turn, so the
 * text still reads left to right on the screen.
 */
export function layoutAlongPath(
  text: string, m: GlyphMetrics, pts: readonly Pt[],
  opts: AlongOpts = {},
): GlyphPlace[] {
  const out = runScratch();
  layoutAlongPathInto(text, m, pts, opts, out);
  return out.glyphs;
}

/** What a run along a path takes. */
export interface AlongOpts {
  anchor?: Anchor;
  startS?: number;
  letterSpacing?: number;
  upright?: boolean;
  closed?: boolean;
}

/** The one point every walk of a path writes into. */
const AT = { x: 0, y: 0, rot: 0 };

/** `layoutAlongPath` into a scratch: the frame path's form. */
export function layoutAlongPathInto(
  text: string, m: GlyphMetrics, pts: readonly Pt[],
  opts: AlongOpts, out: RunScratch,
): void {
  out.n = 0;
  out.width = 0;
  if (text === '' || pts.length === 0) return;
  const closed = opts.closed ?? false;
  const letterSpacing = opts.letterSpacing ?? 0;
  const width = runWidth(text, m, letterSpacing);
  out.width = width;
  const s0 = (opts.startS ?? 0) + penStart(opts.anchor ?? 'left', width);
  const total = polylineLength(pts, closed);

  const flip = (opts.upright ?? true) && meanCos(pts, total, s0, width, closed) < 0;

  let pen = s0;
  // The pen walks the path forwards either way. A flipped run puts the LAST
  // character at the smallest arc length, so the reader, who takes the path
  // the other way, meets the first character first.
  if (!flip) {
    for (const ch of text) {
      const adv = m.advance(ch);
      placeAlong(out, pts, total, closed, ch, pen + adv / 2, flip);
      pen += adv + letterSpacing;
    }
    return;
  }
  // Last character first, by code point: a low surrogate joins the high one
  // before it, exactly as the forward walk joins the two.
  for (let i = text.length; i > 0;) {
    const w = i >= 2 && isLowSurrogate(text.charCodeAt(i - 1))
      && isHighSurrogate(text.charCodeAt(i - 2)) ? 2 : 1;
    i -= w;
    const ch = text.slice(i, i + w);
    const adv = m.advance(ch);
    placeAlong(out, pts, total, closed, ch, pen + adv / 2, flip);
    pen += adv + letterSpacing;
  }
}

function isHighSurrogate(code: number): boolean { return code >= 0xd800 && code <= 0xdbff; }
function isLowSurrogate(code: number): boolean { return code >= 0xdc00 && code <= 0xdfff; }

/** One glyph `s` along the path, turned to the tangent, or half a turn more when the run is flipped. */
function placeAlong(
  out: RunScratch, pts: readonly Pt[], total: number, closed: boolean,
  ch: string, s: number, flip: boolean,
): void {
  pointAlongOf(pts, total, s, AT, closed);
  place(out, ch, AT.x, AT.y, wrapAngle(flip ? AT.rot + Math.PI : AT.rot));
}

/** The mean cosine of the tangent over the span a run covers. */
function meanCos(
  pts: readonly Pt[], total: number, s0: number, width: number, closed: boolean,
): number {
  let sum = 0;
  for (let i = 0; i < UPRIGHT_SAMPLES; i++) {
    pointAlongOf(pts, total, s0 + (width * i) / (UPRIGHT_SAMPLES - 1), AT, closed);
    sum += Math.cos(AT.rot);
  }
  return sum / UPRIGHT_SAMPLES;
}
